import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import { LoginCredentials, LoginResponse } from '../types/auth';
import { clearLegacyAuthStorage, hasSessionHint, persistCsrfToken, stripAuthorizationHeader, withCsrfHeader } from '../utils/authSession';

// Custom error class for API errors
class ApiError extends Error {
  status?: number;
  isAxiosError: boolean = true;
  
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

class ApiService {
  private api: AxiosInstance;
  private isRedirecting: boolean = false; // Prevent multiple redirects
  private lastRequestTime: number = 0;
  private requestCount: number = 0;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1',
      headers: {
          'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      withCredentials: true
    });

    // Add request interceptor for auth token
    this.api.interceptors.request.use(
      (config) => {
        const originalUrl = String(config.url || '');

        // Prevent requests if we're already redirecting to login
        if (
          this.isRedirecting &&
          !originalUrl.includes('/auth/login') &&
          !originalUrl.includes('/auth/signup') &&
          !originalUrl.includes('/auth/refresh')
        ) {
          return Promise.reject(new Error('Session expired, redirecting to login'));
        }

        // Basic rate limiting on client side
        const now = Date.now();
        if (now - this.lastRequestTime < 100) {
          this.requestCount++;
          if (this.requestCount > 10) {
            console.warn('Too many requests in short period, throttling...');
            return Promise.reject(new Error('Client-side rate limit'));
          }
        } else {
          this.requestCount = 0;
        }
        this.lastRequestTime = now;

        config.headers = stripAuthorizationHeader(config.headers || {});
        const method = String(config.method || 'GET').toUpperCase();
        if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
          config.headers = withCsrfHeader(config.headers);
        }
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => {
        const csrfToken = response?.data?.csrf_token;
        if (typeof csrfToken === 'string' && csrfToken.trim()) {
          persistCsrfToken(csrfToken);
        }
        return response;
      },
      async (error) => {
        const status = error.response?.status;
        const originalRequest = error.config || {};
        const originalUrl = String(originalRequest.url || '');

        if (
          status === 401 &&
          hasSessionHint() &&
          !this.isRedirecting &&
          !originalRequest._retry &&
          !originalUrl.includes('/auth/login') &&
          !originalUrl.includes('/auth/refresh')
        ) {
          originalRequest._retry = true;
          try {
            await this.refreshSession();
            return this.api(originalRequest);
          } catch {
            clearLegacyAuthStorage();
            localStorage.removeItem('user');
            if (window.location.pathname !== '/login') {
              this.isRedirecting = true;
              window.location.replace('/login');
            } else {
              this.isRedirecting = false;
            }
            return Promise.reject(new Error('SESSION_EXPIRED'));
          }
        }
        
        // Handle 429 Too Many Requests
        if (status === 429) {
          console.warn('Rate limited by server, please wait before retrying');
          return Promise.reject(new Error('RATE_LIMITED'));
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Reset redirect flag (called after successful login)
  resetRedirectFlag() {
    this.isRedirecting = false;
  }

  private async refreshSession(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = (async () => {
        await this.api.post('/auth/refresh', {}, { headers: withCsrfHeader({}) });
        await new Promise((resolve) => window.setTimeout(resolve, 75));
      })().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  async waitForSessionReady(maxAttempts: number = 4): Promise<void> {
    const delays = [0, 75, 150, 300];
    let lastError: unknown = null;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const delay = delays[Math.min(attempt, delays.length - 1)];
      if (delay > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, delay));
      }
      try {
        await this.api.get('/auth/me');
        return;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError instanceof Error ? lastError : new Error('SESSION_NOT_READY');
  }

  setAuthToken(token: string) {
    void token;
  }

  removeAuthToken() {
    clearLegacyAuthStorage();
  }

  async request<T>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.request(config);
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  async post<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  async put<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      // Check if identifier is an email using simple regex
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.identifier);
      
      // Format the request body according to the backend expectations
      const requestBody = {
        email: isEmail ? credentials.identifier : undefined,
        whatsappNumber: !isEmail ? credentials.identifier : undefined,
        password: credentials.password
      };

      const response = await this.api.post<LoginResponse>('/auth/login', requestBody);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Preserve the error type and message from the backend
        throw new ApiError(
          error.response?.data?.detail || 'Login failed',
          error.response?.status
        );
      }
      throw error instanceof Error ? error : new Error('An unexpected error occurred');
    }
  }

  async logout(): Promise<void> {
    await this.post('/auth/logout', {}, { headers: withCsrfHeader({}) });
    return;
  }

  async getAnalytics<T>(): Promise<T> {
    const response = await this.get<T>('/analytics');
    return response.data;
  }
}

export const apiService = new ApiService();
